import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Check, ExternalLink, Loader2, X } from 'lucide-react';
import { useId } from 'react';
import { Link } from 'react-router-dom';

import {
  Confirmation,
  ConfirmationAccepted,
  ConfirmationAction,
  ConfirmationActions,
  ConfirmationRejected,
  ConfirmationRequest,
  ConfirmationTitle,
} from '@/components/ai-elements';
import type { SuspendedRun } from '@/hooks/usePendingWorkflows';
import { cn } from '@/lib/utils';
import {
  canResumeWorkflowFromChat,
  isInvoiceWorkflowName,
  mirandaRichApi,
  type WorkflowPointer,
  type WorkflowResumeResult,
} from '@/services/miranda-rich-chat';

import { useDecision, type DecisionController } from './decision';
import { CARD_ACTION_CLASS, STREAMING_HINT, invalidateAfterWrite } from './proposal-card';

export type WorkflowAction = 'approve' | 'reject';

/**
 * La clau de la consulta de pendents d'una targeta. Comença per `['mastra', 'pending']`, com la
 * de `usePendingWorkflows`: invalidar aquell prefix les refresca totes dues. Porta el `runId`
 * perquè cada targeta demani la llista en muntar-se: el run d'un workflow acabat d'obrir encara
 * no és a la còpia que la insígnia del menú refresca cada 10 segons.
 */
export const pendingRunQueryKey = (runId: string) => ['mastra', 'pending', { runId }] as const;

const WORKFLOW_TITLES: Record<string, string> = {
  'cancel-reservation': 'Cancel·lació d\'una reserva',
  'process-invoice': 'Factura per aprovar',
  'process-invoice-pdf': 'Factura per aprovar',
  'conciliate-expenses': 'Conciliació per aprovar',
};

/** El workflow, pel run de `/mastra/pending` si hi és, o pel punter. */
function workflowNameOf(pointer: WorkflowPointer, run: SuspendedRun | undefined): string {
  return run?.workflowName ?? pointer.workflowId ?? '';
}

/** Les quatre dades d'una factura que calen per decidir. El NIF ve de `/mastra/pending`. */
function InvoiceSummary({ summary }: { summary: Record<string, any> }) {
  const rows: Array<[string, string | undefined]> = [
    ['Proveïdor', summary.providerTradename || summary.providerName],
    ['NIF', summary.providerNif],
    ['Número', summary.documentNumber || 'sense número'],
    ['Data', summary.date || summary.documentDate],
    ['Total', typeof summary.totalAmount === 'number' ? `${summary.totalAmount.toFixed(2)} €` : undefined],
  ];
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm" data-testid="workflow-invoice-summary">
      {rows
        .filter(([, value]) => value !== undefined && value !== '')
        .map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="min-w-0 break-words font-medium">{value}</dd>
          </div>
        ))}
    </dl>
  );
}

/**
 * El que es cancel·la: la reserva i l'hoste, com a `/admin/workflows`. Les dates són al missatge
 * del workflow, que la targeta pinta just a sobre. El workflow de cancel·lació no calcula cap
 * import ni cap penalització: no n'hi ha cap per ensenyar.
 */
function CancellationSummary({ info }: { info: SuspendedRun['displayInfo'] }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm" data-testid="workflow-cancel-summary">
      <dt className="text-muted-foreground">Reserva</dt>
      <dd className="min-w-0 break-all font-mono text-xs">{info.reservationId}</dd>
      {info.guestName && (
        <>
          <dt className="text-muted-foreground">Client</dt>
          <dd className="min-w-0 break-words font-medium">{info.guestName}</dd>
        </>
      )}
    </dl>
  );
}

/**
 * Hi ha botons? Només per a la cancel·lació d'una reserva, i només si el run diu quina reserva és
 * i porta el missatge del workflow (hoste i dates): sense això a la vista no s'aprova res.
 */
export function canDecideInChat(run: SuspendedRun | undefined): run is SuspendedRun {
  return (
    !!run &&
    canResumeWorkflowFromChat(run.workflowName) &&
    !!run.displayInfo?.reservationId &&
    !!run.displayInfo?.message
  );
}

/** Les dues crides que la targeta fa. Es poden substituir als tests. */
export interface WorkflowActions {
  resume: (run: SuspendedRun, approved: boolean) => Promise<WorkflowResumeResult>;
}

const DEFAULT_ACTIONS: WorkflowActions = { resume: mirandaRichApi.resumeWorkflow };

/**
 * «Aprova» o «Rebutja» d'un workflow. Fora del component perquè es pugui provar sense DOM: dos
 * clics seguits passen dues vegades per aquí i només el primer envia res (`DecisionController`).
 *
 * Un workflow que no es decideix des del xat (una factura, una conciliació) no envia res, el
 * cridi qui el cridi: torna `false` sense tocar l'estat.
 */
export function decideWorkflow({
  controller,
  actions,
  queryClient,
  run,
  action,
}: {
  controller: DecisionController<WorkflowAction, WorkflowResumeResult>;
  actions: WorkflowActions;
  queryClient: QueryClient;
  run: SuspendedRun;
  action: WorkflowAction;
}): boolean {
  if (!canDecideInChat(run)) return false;
  return controller.run(action, async () => {
    const result = await actions.resume(run, action === 'approve');
    void queryClient.invalidateQueries({ queryKey: ['mastra', 'pending'] });
    // Aprovar escriu (cancel·la la reserva): el calendari, els llistats i la fitxa oberta.
    if (action === 'approve') invalidateAfterWrite(queryClient);
    // El resume respon 200 també quan el pas falla: l'error ve dins del cos.
    const failure =
      result?.status === 'failed' || result?.result?.success === false
        ? result?.error || result?.result?.message || 'El workflow ha fallat'
        : undefined;
    if (failure) throw new Error(failure);
    return result;
  });
}

export interface WorkflowApprovalCardProps {
  /** El punter que ha arribat per l'stream (`data-workflow`): només el `runId` i el workflow. */
  pointer: WorkflowPointer;
  actions?: WorkflowActions;
  /** Miranda encara escriu el missatge: «Aprova» i «Rebutja» desactivats (com a `ProposalCard`). */
  isStreaming?: boolean;
  className?: string;
}

/**
 * Targeta d'un workflow que espera aprovació (factura, cancel·lació), dins del xat.
 *
 * Per l'stream només n'arriba el punter. El detall (el nom de l'hoste, el proveïdor i el seu
 * NIF, l'import) es demana aquí a `GET /mastra/pending`, que és on ja es llegia a
 * `/admin/workflows`, i s'hi busca el `runId`.
 *
 * **Només la cancel·lació d'una reserva té «Aprova» i «Rebutja»**, contra `POST /mastra/resume`:
 * no demana cap tria i la targeta ensenya el que es cancel·la. Una factura ensenya el resum i
 * l'enllaç a `/admin/workflows`, sense botons: aprovar-la crea una despesa a Cuentica amb un
 * compte, un tipus de despesa i una forma de pagament que aquí no es veuen ni es poden triar.
 * Una conciliació (cal triar moviments) i qualsevol workflow que el xat no conegui, igual.
 *
 * Si el run ja no és a la llista de pendents, algú l'ha resolt (aquí, a `/admin/workflows` o
 * en una altra pestanya): la targeta ho diu i no ofereix cap botó.
 */
export function WorkflowApprovalCard({
  pointer,
  actions = DEFAULT_ACTIONS,
  isStreaming = false,
  className,
}: WorkflowApprovalCardProps) {
  const queryClient = useQueryClient();
  const hintId = useId();
  const { data: pendingRuns, isLoading, isError } = useQuery({
    queryKey: pendingRunQueryKey(pointer.runId),
    queryFn: mirandaRichApi.getPendingRuns,
    refetchInterval: 10_000,
  });
  const { controller, state } = useDecision<WorkflowAction, WorkflowResumeResult>();

  const run = pendingRuns?.find((item) => item.runId === pointer.runId);
  const workflowName = workflowNameOf(pointer, run);
  const title = WORKFLOW_TITLES[workflowName] ?? 'Aprovació pendent';
  const isInvoice = isInvoiceWorkflowName(workflowName);
  const isConciliation = workflowName === 'conciliate-expenses';
  const isCancellation = canResumeWorkflowFromChat(workflowName);
  const canDecide = canDecideInChat(run);

  const decide = (action: WorkflowAction) => {
    if (canDecideInChat(run)) decideWorkflow({ controller, actions, queryClient, run, action });
  };

  const working = state.phase === 'working';
  const done = state.phase === 'done' ? state : undefined;
  const error = state.phase === 'error' ? state.error : undefined;
  // Pendent mentre el run és a la llista i aquí no s'ha decidit res.
  const pending = !!run && !done;
  const resolvedElsewhere = !run && state.phase === 'idle' && !isLoading && !isError;
  const approvalId = pointer.runId;

  return (
    <Confirmation
      className={cn('min-w-0 max-w-full bg-background px-3', className)}
      data-testid="workflow-approval-card"
      data-run-id={pointer.runId}
      state={pending ? 'approval-requested' : 'approval-responded'}
      approval={
        pending || !done ? { id: approvalId } : { id: approvalId, approved: done.action === 'approve' }
      }
    >
      <ConfirmationTitle className="block space-y-2">
        <span className="block font-medium">{title}</span>
        {isLoading && (
          <span className="inline-flex items-center gap-1.5 text-muted-foreground text-xs">
            <Loader2 className="size-3.5 animate-spin" />
            Carregant el detall…
          </span>
        )}
        {run?.displayInfo?.message && <span className="block text-sm">{run.displayInfo.message}</span>}
        {run && isInvoice && run.displayInfo?.summary && <InvoiceSummary summary={run.displayInfo.summary} />}
        {run && isCancellation && run.displayInfo?.reservationId && (
          <CancellationSummary info={run.displayInfo} />
        )}
      </ConfirmationTitle>

      {isError && (
        <p className="text-destructive text-xs" role="alert">
          No s'ha pogut llegir el detall. Obre'l a Aprovacions.
        </p>
      )}
      {resolvedElsewhere && (
        <p className="text-muted-foreground text-xs" data-testid="workflow-resolved">
          Ja no espera aprovació: s'ha resolt o ha caducat.
        </p>
      )}

      <ConfirmationRequest>
        <p id={hintId} className="text-muted-foreground text-xs" data-testid="workflow-request-note">
          {canDecide
            ? `Encara no s'ha fet res. ${isStreaming ? STREAMING_HINT : 'Espera la teva decisió'}.`
            : isInvoice
              ? 'Les factures s\'aproven a Aprovacions, on es tria el compte i la forma de pagament.'
              : isConciliation
                ? 'Una conciliació s\'aprova triant els moviments, a Aprovacions.'
                : isCancellation
                  ? 'Falta el detall de la reserva: aprova-la o rebutja-la a Aprovacions.'
                  : 'Aquest workflow s\'aprova a Aprovacions.'}
        </p>
      </ConfirmationRequest>
      <ConfirmationAccepted>
        <p className="inline-flex items-center gap-1.5 text-green-700 text-xs dark:text-green-500">
          <Check className="size-3.5" />
          Aprovat{done?.result?.result?.message ? `: ${done.result.result.message}` : ''}
        </p>
      </ConfirmationAccepted>
      <ConfirmationRejected>
        <p className="inline-flex items-center gap-1.5 text-muted-foreground text-xs">
          <X className="size-3.5" />
          Rebutjat: no s'ha fet res
        </p>
      </ConfirmationRejected>

      {error && <p className="text-destructive text-xs" role="alert">{error}</p>}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link
          to="/admin/workflows"
          className="inline-flex min-h-10 items-center gap-1 text-muted-foreground text-xs underline-offset-2 hover:underline"
        >
          <ExternalLink className="size-3" />
          Obre a Aprovacions
        </Link>
        {canDecide && (
          <ConfirmationActions className="flex-wrap self-auto">
            <ConfirmationAction
              variant="outline"
              className={CARD_ACTION_CLASS}
              disabled={working || isStreaming}
              aria-describedby={isStreaming ? hintId : undefined}
              onClick={() => decide('reject')}
            >
              Rebutja
            </ConfirmationAction>
            <ConfirmationAction
              className={CARD_ACTION_CLASS}
              disabled={working || isStreaming}
              aria-describedby={isStreaming ? hintId : undefined}
              onClick={() => decide('approve')}
            >
              {working && <Loader2 className="size-3.5 animate-spin" />}
              Aprova
            </ConfirmationAction>
          </ConfirmationActions>
        )}
      </div>
    </Confirmation>
  );
}
